/**
 * EnfactImportsPage - Import de factures Enfact
 * Permet d'importer des factures creees dans Enfact et de deduire le stock
 */
import { useState, useEffect, useCallback } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { usePOSStore } from '../hooks/usePOSStore';
import { api } from '../services/api';
import { formatPrice } from '../utils/pos.utils';
import { logger } from '../utils/logger';
import ImportMappingModal from '../components/enfact/ImportMappingModal';

// Types
interface EnfactInvoice {
  id: number;
  number: string;
  date: string;
  customer_name: string;
  total_excl_vat: number;
  total_incl_vat: number;
  status: string;
}

interface ImportedInvoice {
  id: string;
  enfact_invoice_id: number;
  enfact_invoice_number?: string;
  import_status: 'pending_mapping' | 'pending_validation' | 'validated' | 'stock_deducted' | 'completed';
  enfact_customer_name?: string;
  enfact_total_excl_vat?: number;
  enfact_total_incl_vat?: number;
  enfact_date?: string;
  lines: ImportedLine[];
  created_at: string;
}

interface ImportedLine {
  id: string;
  line_number: number;
  description: string;
  quantity: number;
  unit_price: number;
  vat_rate: number;
  matched_product_id?: string;
  matched_product_name?: string;
  matched_product_stock_total?: number;
  matched_product_stock_display?: number;
  match_status: 'pending' | 'matched' | 'confirmed' | 'skipped';
  match_confidence?: number;
  suggested_products?: Array<{
    product_id: string;
    product_name: string;
    confidence: number;
    stock_total?: number;
    stock_display?: number;
  }>;
  stock_deducted: boolean;
}

type TabFilter = 'available' | 'pending' | 'completed';

export default function EnfactImportsPage() {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const vendorId = usePOSStore((state) => state.vendorId);
  // Page dashboard (ADMIN/MANAGER) : l'identite vient de l'utilisateur authentifie.
  // Repli sur vendorId si la page est ouverte depuis un contexte POS.
  const userId = user?.id || vendorId || '';
  const [activeTab, setActiveTab] = useState<TabFilter>('available');
  const [availableInvoices, setAvailableInvoices] = useState<EnfactInvoice[]>([]);
  const [importedInvoices, setImportedInvoices] = useState<ImportedInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImport, setSelectedImport] = useState<ImportedInvoice | null>(null);

  const enfactEnabled = Boolean(tenant?.config?.integrations?.enfact?.enabled);

  // Charger les factures disponibles depuis Enfact
  const loadAvailableInvoices = useCallback(async () => {
    if (!tenant?.id) return;
    try {
      const data = await api.get(`/enfact/${tenant.id}/import/available`);
      setAvailableInvoices(data || []);
    } catch (err: any) {
      logger.error('[EnfactImportsPage] Erreur chargement disponibles:', err);
    }
  }, [tenant?.id]);

  // Charger les factures importees
  const loadImportedInvoices = useCallback(async () => {
    if (!tenant?.id) return;
    try {
      const data = await api.get(`/enfact/${tenant.id}/import/pending`);
      setImportedInvoices(data || []);
    } catch (err: any) {
      logger.error('[EnfactImportsPage] Erreur chargement importees:', err);
    }
  }, [tenant?.id]);

  // Charger les donnees
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadAvailableInvoices(), loadImportedInvoices()]);
    } catch (err: any) {
      setError('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
    }
  }, [loadAvailableInvoices, loadImportedInvoices]);

  useEffect(() => {
    if (enfactEnabled) {
      loadData();
    }
  }, [enfactEnabled, loadData]);

  // Synchroniser depuis Enfact
  const handleSync = async () => {
    if (!tenant?.id) return;
    setSyncing(true);
    try {
      const result = await api.post(`/enfact/${tenant.id}/import/sync`, {});
      logger.log('[EnfactImportsPage] Sync result:', result);
      await loadData();
      if (result.imported > 0) {
        setActiveTab('pending');
      }
    } catch (err: any) {
      logger.error('[EnfactImportsPage] Erreur sync:', err);
      setError(err.response?.data?.message || 'Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  // Ouvrir le modal de mapping
  const handleOpenMapping = (invoice: ImportedInvoice) => {
    setSelectedImport(invoice);
  };

  // Fermer le modal
  const handleCloseMapping = () => {
    setSelectedImport(null);
    loadImportedInvoices();
  };

  // Filtrer selon l'onglet
  const pendingImports = importedInvoices.filter(
    (inv) => inv.import_status === 'pending_mapping' || inv.import_status === 'pending_validation' || inv.import_status === 'validated'
  );
  const completedImports = importedInvoices.filter(
    (inv) => inv.import_status === 'stock_deducted' || inv.import_status === 'completed'
  );

  // Affichage du statut
  const getStatusBadge = (status: ImportedInvoice['import_status']) => {
    const badges = {
      pending_mapping: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Mapping requis' },
      pending_validation: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'A valider' },
      validated: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Valide' },
      stock_deducted: { bg: 'bg-green-100', text: 'text-green-800', label: 'Stock deduit' },
      completed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Traitee (sans stock)' },
    };
    const badge = badges[status];
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  if (!enfactEnabled) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Import Factures Enfact</h1>
          <p className="mt-2 text-gray-600">Synchronisation et gestion du stock</p>
        </div>
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-amber-900 mb-2">Enfact non active</h2>
          <p className="text-amber-700">
            L'integration Enfact n'est pas activee pour ce tenant.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Import Factures Enfact</h1>
          <p className="mt-2 text-gray-600">Synchronisation et deduction du stock</p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing || loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium flex items-center gap-2"
        >
          {syncing ? (
            <>
              <span className="animate-spin">⟳</span>
              Synchronisation...
            </>
          ) : (
            <>
              <span>↻</span>
              Synchroniser depuis Enfact
            </>
          )}
        </button>
      </div>

      {/* Erreur */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('available')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'available'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Disponibles ({availableInvoices.length})
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pending'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            En cours ({pendingImports.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'completed'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Termines ({completedImports.length})
          </button>
        </nav>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin text-4xl mb-4">⟳</div>
          <p className="text-gray-500">Chargement...</p>
        </div>
      ) : (
        <>
          {/* Tab: Disponibles */}
          {activeTab === 'available' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {availableInvoices.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="text-4xl mb-4">📄</div>
                  <p>Aucune nouvelle facture a importer</p>
                  <p className="text-sm mt-2">Cliquez sur "Synchroniser" pour verifier</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Numero</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total HT</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total TTC</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {availableInvoices.map((inv) => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-mono text-sm">{inv.number}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(inv.date).toLocaleDateString('fr-BE')}
                        </td>
                        <td className="px-6 py-4 text-sm">{inv.customer_name}</td>
                        <td className="px-6 py-4 text-sm text-right">{formatPrice(inv.total_excl_vat * 100)}</td>
                        <td className="px-6 py-4 text-sm text-right font-medium">{formatPrice(inv.total_incl_vat * 100)}</td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-gray-400 text-sm">Import auto via sync</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Tab: En cours */}
          {activeTab === 'pending' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {pendingImports.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="text-4xl mb-4">📋</div>
                  <p>Aucune facture en cours de traitement</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Numero</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total TTC</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pendingImports.map((inv) => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-mono text-sm">{inv.enfact_invoice_number || inv.enfact_invoice_id}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {inv.enfact_date ? new Date(inv.enfact_date).toLocaleDateString('fr-BE') : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">{inv.enfact_customer_name || '-'}</td>
                        <td className="px-6 py-4 text-sm text-right font-medium">
                          {inv.enfact_total_incl_vat ? formatPrice(inv.enfact_total_incl_vat) : '-'}
                        </td>
                        <td className="px-6 py-4 text-center">{getStatusBadge(inv.import_status)}</td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => handleOpenMapping(inv)}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm font-medium"
                          >
                            Traiter
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Tab: Termines */}
          {activeTab === 'completed' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {completedImports.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="text-4xl mb-4">✅</div>
                  <p>Aucune facture traitee</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Numero</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total TTC</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {completedImports.map((inv) => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-mono text-sm">{inv.enfact_invoice_number || inv.enfact_invoice_id}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {inv.enfact_date ? new Date(inv.enfact_date).toLocaleDateString('fr-BE') : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">{inv.enfact_customer_name || '-'}</td>
                        <td className="px-6 py-4 text-sm text-right font-medium">
                          {inv.enfact_total_incl_vat ? formatPrice(inv.enfact_total_incl_vat) : '-'}
                        </td>
                        <td className="px-6 py-4 text-center">{getStatusBadge(inv.import_status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {/* Modal de mapping */}
      {selectedImport && (
        <ImportMappingModal
          invoice={selectedImport}
          tenantId={tenant?.id || ''}
          userId={userId}
          onClose={handleCloseMapping}
        />
      )}
    </div>
  );
}
